import { useEffect, useState } from 'react';
import { staffApi, formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

const PAYMENT_METHODS = ['CASH', 'CARD', 'INTERAC', 'E_TRANSFER'];

// Staff processes an immediate in-store top-up: payment + credit/expiry extension applied in one step.
export function TopUpDialog({ open, onOpenChange, subscriptionId, currentPlanId, customerName, onDone }) {
  const [plans, setPlans] = useState(null);
  const [planId, setPlanId] = useState('');
  const [method, setMethod] = useState('CASH');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    staffApi.get('/plans/public').then(({ data }) => {
      setPlans(data.plans);
      setPlanId(data.plans.find((p) => p.id === currentPlanId)?.id || data.plans[0]?.id || '');
    });
  }, [open, currentPlanId]);

  const selectedPlan = plans?.find((p) => p.id === planId);

  const confirm = async () => {
    if (!planId) return;
    setBusy(true);
    try {
      await staffApi.post(`/subscriptions/${subscriptionId}/topup`, { planId, method, referenceNumber, notes });
      toast.success('Top-up confirmed — tiffins and expiry extended');
      setReferenceNumber('');
      setNotes('');
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="staff-topup-dialog">
        <DialogHeader>
          <DialogTitle>Top Up {customerName}'s Tiffins</DialogTitle>
          <DialogDescription>Pick the plan they're paying for in-store — credits and expiry extend immediately.</DialogDescription>
        </DialogHeader>

        {plans === null ? (
          <p className="text-sm text-muted-ink">Loading plans…</p>
        ) : (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Plan</Label>
              <Select value={planId} onValueChange={setPlanId}>
                <SelectTrigger data-testid="staff-topup-plan-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {plans.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name} — {p.tiffinCount} tiffins / {p.validityDays}d — ${Number(p.price).toFixed(2)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Payment Method</Label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger data-testid="staff-topup-method-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map((m) => <SelectItem key={m} value={m}>{m.replace('_', ' ')}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Payment Reference (optional)</Label>
              <Input data-testid="staff-topup-reference-input" value={referenceNumber} onChange={(e) => setReferenceNumber(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Notes (optional)</Label>
              <Textarea data-testid="staff-topup-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
            {selectedPlan && (
              <div className="rounded-xl bg-bone p-3 flex justify-between text-sm font-semibold">
                <span>Amount Due</span>
                <span data-testid="staff-topup-amount">${Number(selectedPlan.price).toFixed(2)}</span>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button data-testid="staff-topup-confirm-btn" disabled={!planId || busy} onClick={confirm} className="bg-saffron hover:bg-saffron-hover">
            {busy ? 'Processing…' : 'Confirm Payment & Top Up'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
